import { useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, ImageIcon } from "lucide-react";
import { Button } from "@ndd/ui";
import { cn } from "@/lib/utils";
import { formatLocalDate, formatTimestamp } from "../../../../shared/local-date";
import type { ObservationNoteData } from "../../../../shared/observation-types";
import { ObservationTagBadge } from "./observation-tag";
import { ObservationDeleteDialog } from "./observation-delete-dialog";
import { ObservationImageGallery } from "./observation-image-gallery";
import { AskAboutSourceAction } from "./ask-about-source-action";

interface ObservationRowProps {
  rdId: string;
  rdSlug: string;
  note: ObservationNoteData;
  isExpanded: boolean;
  onToggle: (noteId: string) => void;
  onEdit: (note: ObservationNoteData) => void;
  onDeleted: () => void;
}

export function ObservationRow({
  rdId,
  rdSlug,
  note,
  isExpanded,
  onToggle,
  onEdit,
  onDeleted,
}: ObservationRowProps) {
  const edited = note.updatedAt !== note.createdAt;
  const rowRef = useRef<HTMLDivElement>(null);

  // On expand, bring the row (and its newly-revealed detail) into view within
  // the scrollable list.
  useEffect(() => {
    if (isExpanded) {
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isExpanded]);

  return (
    <div ref={rowRef}>
      <button
        type="button"
        onClick={() => onToggle(note.id)}
        className={cn(
          "flex w-full items-start gap-3 border-b border-l-4 border-sand-200 px-5 py-3 text-left transition-colors",
          isExpanded
            ? "border-l-interactive bg-sky-100/30"
            : "border-l-transparent hover:bg-sand-100",
        )}
        aria-expanded={isExpanded}
      >
        {/* Observation date */}
        <span className="w-[64px] shrink-0 pt-0.5 text-sm text-sand-500">
          {formatLocalDate(note.observationDate)}
        </span>

        {/* Fixed-width tag slot so titles align across rows */}
        <span className="w-[84px] shrink-0 pt-0.5">
          <ObservationTagBadge tag={note.tag} />
        </span>

        {/* Main text */}
        <span className="min-w-0 flex-1">
          {note.title ? (
            <>
              <span className="block text-sm font-semibold text-foreground">
                {note.title}
              </span>
              {!isExpanded && (
                <span className="mt-0.5 block text-[13px] text-sand-500 line-clamp-1">
                  {note.body}
                </span>
              )}
            </>
          ) : (
            <span className="block text-[13px] text-sand-500 line-clamp-1">
              {note.body}
            </span>
          )}
        </span>

        {note.images.length > 0 && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-sand-100 px-2 py-1 text-[11px] font-semibold text-sand-500">
            <ImageIcon className="h-3.5 w-3.5" />
            {note.images.length}
          </span>
        )}

        <span className="shrink-0 pt-0.5 text-sand-300">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-b border-sand-200 bg-sand-50 px-5 pb-[18px] pl-[38px] pt-4">
          <div className="flex items-center gap-3 text-xs text-sand-400">
            <ObservationTagBadge tag={note.tag} />
            <span>Observed {formatLocalDate(note.observationDate, "shortWithYear")}</span>
          </div>
          <p className="my-3 max-w-[680px] whitespace-pre-wrap text-sm leading-relaxed text-sand-600">
            {note.body}
          </p>
          {note.images.length > 0 && (
            <ObservationImageGallery
              rdId={rdId}
              noteId={note.id}
              images={note.images}
              className="mb-3 max-w-[680px]"
            />
          )}
          <div className="flex items-center gap-2 text-xs text-sand-400">
            <span>
              Logged {formatTimestamp(note.createdAt)}
              {note.authorName ? ` · by ${note.authorName}` : ""}
              {edited ? ` · edited ${formatTimestamp(note.updatedAt)}` : ""}
            </span>
          </div>
          <div className="mt-3.5 flex gap-2 py-2">
            <AskAboutSourceAction rdSlug={rdSlug} source={{ type: "observation", id: note.id }} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(note)}
              className="h-8 rounded-[10px] border-sand-200 px-3 text-xs font-medium"
            >
              Edit
            </Button>
            <ObservationDeleteDialog
              rdId={rdId}
              noteId={note.id}
              onDeleted={onDeleted}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-[10px] border-error px-3 text-xs font-medium text-error hover:bg-error-light hover:text-error"
                >
                  Delete
                </Button>
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
